import type { TFunction } from 'i18next';
import { BoxesIcon, BoxIcon, FileIcon, FileSpreadsheetIcon, FileTextIcon, ImageIcon } from 'lucide-react';
import { type DataUpload, DataUploadType } from '../../common/data-upload';

export const getDataUploadTypeIconConfig = (dataUpload: DataUpload) => {
	if (isTilesetDataUpload(dataUpload)) {
		return { icon: BoxesIcon, color: 'text-emerald-600' };
	}
	if (isCeisiumIdDataUpload(dataUpload)) {
		return { icon: BoxIcon, color: 'text-purple-600' };
	}
	if (isImageFileDataUpload(dataUpload)) {
		return { icon: ImageIcon, color: 'text-blue-600' };
	}
	if (isPdfFileDataUpload(dataUpload)) {
		return { icon: FileTextIcon, color: 'text-red-600' };
	}
	if (isSpreadsheetFileDataUpload(dataUpload)) {
		return { icon: FileSpreadsheetIcon, color: 'text-green-600' };
	}
	if (isTextDocumentDataUpload(dataUpload)) {
		return { icon: FileTextIcon, color: 'text-slate-600' };
	}
	return { icon: FileIcon, color: 'text-gray-600' };
};

export const isCeisiumIdDataUpload = (dataUpload: DataUpload): boolean => {
	return dataUpload.uploadType === DataUploadType.CESIUM_ID;
};
export const isTilesetDataUpload = (dataUpload: DataUpload): boolean => {
	return dataUpload.uploadType === DataUploadType.TILESET;
};
export const isFileDataUpload = (dataUpload: DataUpload): boolean => {
	return dataUpload.uploadType === DataUploadType.FILE;
};
export const isImageFileDataUpload = (dataUpload: DataUpload): boolean => {
	return dataUpload.fileType?.startsWith('image/') || false;
};
export const isPdfFileDataUpload = (dataUpload: DataUpload): boolean => {
	return dataUpload.fileType === 'application/pdf';
};

// MIME types the browser reports for the document formats the backend accepts
// (see ALLOWED_EXTENSIONS in file-upload.service.ts).
const SPREADSHEET_FILE_TYPES = new Set([
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	'application/vnd.ms-excel',
	'text/csv',
]);
const TEXT_DOCUMENT_FILE_TYPES = new Set([
	'text/plain',
	'application/msword',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);
export type DataUploadFileType =
	| 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
	| 'application/vnd.ms-excel'
	| 'text/csv'
	| 'text/plain'
	| 'application/msword'
	| 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export const fileTypeLabels = (t: TFunction): Record<DataUploadFileType, string> => ({
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': t('files.types.excel'),
	'application/vnd.ms-excel': t('files.types.excel'),
	'text/csv': t('files.types.csv'),
	'text/plain': t('files.types.text'),
	'application/msword': t('files.types.word'),
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document': t('files.types.word'),
});

export const isSpreadsheetFileDataUpload = (dataUpload: DataUpload): boolean =>
	SPREADSHEET_FILE_TYPES.has(dataUpload.fileType ?? '');
export const isTextDocumentDataUpload = (dataUpload: DataUpload): boolean =>
	TEXT_DOCUMENT_FILE_TYPES.has(dataUpload.fileType ?? '');
/** Human label for a file's MIME type, falling back to the generic "File" label when unknown. */
export const fileTypeLabel = (t: TFunction, fileType?: string | null): string =>
	(fileType && fileTypeLabels(t)[fileType as DataUploadFileType]) || t('common.labels.file');
